import { useEffect, useState } from 'react';
import { Input } from '@verlo/shared/components/ui/input';
import { Label } from '@verlo/shared/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@verlo/shared/components/ui/select';
import { ShieldAlert } from 'lucide-react';
import { authApi } from '@verlo/shared/services/api';

// Who can authorise from the picker — mirrors ManagerActionDialog / ManagerOverrideDialog.
// The server (`pin_auth.authorise_manager_pin`) is the one that enforces it.
const AUTHORISING_ROLES = new Set([
  'shift_lead', 'manager', 'general_manager', 'area_manager', 'operations', 'owner',
]);
const LEGACY_AUTHORISING_ROLES = new Set(['manager', 'admin', 'owner', 'super_admin']);
const canAuthorise = (u) =>
  AUTHORISING_ROLES.has((u.rbac_role || '').toLowerCase()) ||
  LEGACY_AUTHORISING_ROLES.has((u.role || '').toLowerCase());

/**
 * The manager step-up a cash action needs when the signed-in user can't authorise it
 * themselves: pick the manager, enter their PIN. Returns the state the dialog reads and
 * the fields to draw.
 *
 *   const stepUp = useManagerStepUp({ open, needed: !canManage, siteId });
 *   …
 *   <ManagerStepUpFields {...stepUp} testId="till-open" />
 *   …
 *   await cashApi.openTill({ float_opening, ...stepUp.creds() });
 */
export const useManagerStepUp = ({ open, needed, siteId }) => {
  const [managerId, setManagerId] = useState('');
  const [pin, setPin] = useState('');
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (open) { setManagerId(''); setPin(''); } }, [open]);

  useEffect(() => {
    if (!open || !needed) return undefined;
    let cancelled = false;
    setLoading(true);
    authApi.pinStaffGrid(siteId)
      .then((res) => { if (!cancelled) setManagers((res.data?.users || []).filter(canAuthorise)); })
      .catch(() => { if (!cancelled) setManagers([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, needed, siteId]);

  const noManagers = needed && !loading && managers.length === 0;
  const ready = !needed || (!!managerId && pin.length >= 4);
  const creds = () => (needed ? { manager_id: managerId, manager_pin: pin } : {});
  const clearPin = () => setPin('');
  return { needed, managerId, setManagerId, pin, setPin, managers, loading, noManagers, ready, creds, clearPin };
};

export const ManagerStepUpFields = ({ needed, managerId, setManagerId, pin, setPin, managers, loading, noManagers, disabled, testId = 'step-up', line = 'A manager needs to authorise this' }) => {
  if (!needed) return null;
  return (
    <div className="border border-hairline border-l-[3px] border-l-signal-warn-line bg-white p-3 space-y-3" data-testid={`${testId}-step-up`}>
      <p className="font-heading text-[10.5px] font-bold uppercase tracking-[0.06em] text-signal-warn">{line}</p>
      <div className="space-y-1.5">
        <Label className="text-xs">Authorising manager</Label>
        <Select value={managerId} onValueChange={setManagerId} disabled={disabled || loading || noManagers}>
          <SelectTrigger className="h-10 bg-white" data-testid={`${testId}-manager`}>
            <SelectValue placeholder={loading ? 'Loading…' : noManagers ? 'No manager on this site' : 'Select manager'} />
          </SelectTrigger>
          <SelectContent>
            {managers.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs flex items-center gap-1.5"><ShieldAlert size={11} className="text-signal-warn" />Manager PIN</Label>
        <Input
          type="password" inputMode="numeric" pattern="[0-9]*" maxLength={6} autoComplete="off"
          value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
          placeholder="••••"
          className="text-center text-2xl font-mono-display tracking-[0.5em] h-11"
          data-testid={`${testId}-pin`}
          disabled={disabled || noManagers}
        />
      </div>
    </div>
  );
};

/** One line for the toast / inline error from a refused cash write. */
export const cashError = (err, fallback = "Couldn't do that. Try again.") => {
  const status = err?.response?.status;
  const detail = err?.response?.data?.detail;
  if (detail && typeof detail === 'object') return detail.message || fallback;
  if (status === 403) return detail || 'Manager PIN not recognised';
  if (status === 423 || status === 429) return detail || 'Too many tries. Wait a moment, then try again.';
  return typeof detail === 'string' ? detail : fallback;
};
